import { ActivatedRoute } from '@angular/router';

import { loadStringFilterFromGetParams } from '../utils';

import { EventTypeStatus } from './event-type-enums';
import { EntryPassStatus } from './entry-pass-enums';

export enum ApplicationFieldConfig {
  none = 'NON',
  optional = 'OPT',
  required = 'REQ'
}

export interface AutoAddEventType {
  id: number;
  name: string;
  status: EventTypeStatus;
}

export interface AutoAddEventCategory {
  id: number;
  name: string;
}

export interface AutoAddEntryPass {
  id: number;
  name: string;
  status: EntryPassStatus;
}

export interface Association {
  id: number;
  url: string;
  name: string;
  profile_image?: string;
  profile_image_small?: string;
  verification_requirements: string;
  automatic_removal_interval_months?: number;
  automatic_removal_dates: string[];
  reverification_period_days?: number;
  show_reverification_alert: boolean;
  allow_automatic_entry_pass_use: boolean;
  membership_expiration_age: number | null;
  allow_customer_applications: boolean;
  application_document_field_config: ApplicationFieldConfig;
  application_text_field_config: ApplicationFieldConfig;
  application_document_field_name: string;
  application_text_field_name: string;
  application_customer_instructions: string;
  auto_add_on_event_booking_types: AutoAddEventType[];
  auto_add_on_event_booking_categories: AutoAddEventCategory[];
  auto_add_on_event_check_in_types: AutoAddEventType[];
  auto_add_on_event_check_in_categories: AutoAddEventCategory[];
  auto_add_on_entry_pass_use_pass_types: AutoAddEntryPass[];
  auto_add_on_entry_pass_use_only_first: boolean;
  auto_add_on_guest_pass_check_in: boolean;
  auto_add_on_guest_pass_check_in_only_first: boolean;
  member_square_customer_group_id: number | null;
  member_square_customer_group_name: string | null;
  member_lightspeed_customer_group_id: number | null;
  member_lightspeed_customer_group_name: string | null;
  num_members: number;
  alerts_url: string;
  activity_log_url: string;
  created_at: string;
  updated_at: string;
}

export interface AssociationManager {
  id: number;
  url: string;
  association_id: number;
  customer_id: number;
  association_url: string;
  customer_url: string;
  association_name: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_birthday: string;
  created_at: string;
  updated_at: string;
}

export interface AssociationMember {
  id: number;
  url: string;
  association_id: number;
  customer_id: number;
  association_url: string;
  customer_url: string;
  association_name: string;
  association_verification_requirements: string;
  association_reverification_period_days?: number;
  association_shows_reverification_alert: boolean;
  customer_first_name: string;
  customer_last_name: string;
  customer_birthday: string;
  last_reverified_at: string;
  is_pending_reverification: boolean;
  next_automatic_removal_datetime?: string;
  has_pending_application: boolean;
  application_is_hidden_from_inbox: boolean;
  application_document: string | null;
  application_text: string;
  approved_by_id: number | null;
  approved_by_first_name: string | null;
  approved_by_last_name: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssociationAlert {
  id: number;
  url: string;
  alert_text: string;
  expiry_date: string;
  color_hex: string;
  created_by_id: number;
  created_by_email: string;
  updated_by_id: number;
  updated_by_email: string;
  created_date: string;
  updated_date: string;
  created_at: string;
  updated_at: string;
}

export interface AssociationFilters {
  search?: string;
}

export interface AssociationManagerFilters {
  association_id?: number;
  customer_id?: number;
}

export interface AssociationMemberFilters {
  search?: string;
  association_id?: number;
  customer_id?: number;
  exclude_old?: boolean;
  has_pending_application?: boolean;
  application_is_hidden_from_inbox?: boolean;
}

export function loadAssociationFiltersFromGetParams(activatedRoute: ActivatedRoute): AssociationFilters {
  const filters: AssociationFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);

  return filters;
}
